import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Dropdown, Alert, Layout, Menu, Row, Col, Button, Space, message, Empty, Spin, Tag } from "antd";
import { LeftOutlined, RightOutlined, NodeIndexOutlined, RiseOutlined, DashboardOutlined, ThunderboltOutlined, HeartOutlined, CompassOutlined, StarOutlined, ClockCircleOutlined } from "@ant-design/icons";

import moment from "moment";
import _ from "lodash";

import ReactEcharts from "echarts-for-react";
import {
  CANCEL_REQUEST,
  DATE_TIME_FORMAT,
  FILTER_DATE_RANGES,
  FILE_TYPE,
  VIEW_TYPE,
  SPORT_TYPE,
  AREA_CHART_OPTION,
  CHART_COLOR_OPTION
} from "constants/index";

import { AppContext } from "contexts/AppContext";

const AreaChart = ({ chartTitle, chartData, useSearchData }) => {
  const appContext = useContext(AppContext);

  const buildChart = (data) => {
    const option = _.cloneDeep(AREA_CHART_OPTION);
    option.xAxis.data = _.uniqBy(data, "date").map((item, index) => {
      return item.date;
    });
    if (appContext.pageView === VIEW_TYPE.Year && !useSearchData) {
      option.tooltip = {
        trigger: 'axis',
        axisPointer: {
          type: "shadow",
        },
        textStyle: {
          fontSize: 12
        },
        formatter: function (values) {
          let htmlTooltip = `${moment(values[0].name).format(DATE_TIME_FORMAT.MONTH)}<br />`;
          for (let s of values) {
            if (s.seriesName === "Time") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Time}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${(s.data.value / 60 / 60).toFixed(1)} hours<br />`;
            else if (s.seriesName === "Distance") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Distance}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${(s.data.value / 1000).toFixed(1)} km<br />`;
            else if (s.seriesName === "Speed" || s.seriesName === "Pace") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Pace}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.pace}<br />`;
            else if (s.seriesName === "Elevation") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Elevation}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} m<br />`;
            else if (s.seriesName === "Power") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Power}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} watts<br />`;
            else if (s.seriesName === "HR") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.HeartRate}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} bpm`;
          }
          return htmlTooltip;
        }
      };
    }
    else if (useSearchData) {
      option.tooltip = {
        trigger: 'axis',
        axisPointer: {
          type: "shadow",
        },
        textStyle: {
          fontSize: 12
        },
        formatter: function (values) {
          let htmlTooltip = `${moment(values[0].name).format(DATE_TIME_FORMAT.WEEKDAYFULL)}<br />`;
          for (let s of values) {
            if (s.seriesName === "Time") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Time}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${(s.data.value / 60 / 60).toFixed(1)} hours<br />`;
            else if (s.seriesName === "Distance") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Distance}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${(s.data.value / 1000).toFixed(1)} km<br />`;
            else if (s.seriesName === "Speed" || s.seriesName === "Pace") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Pace}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.pace}<br />`;
            else if (s.seriesName === "Elevation") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Elevation}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} m<br />`;
            else if (s.seriesName === "Power") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.Power}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} watts<br />`;
            else if (s.seriesName === "HR") htmlTooltip += `<span style="background-color: ${CHART_COLOR_OPTION.HeartRate}; margin-right: 8px;">&nbsp;</span>${s.seriesName}: ${s.data.value} bpm`;
          }
          return htmlTooltip;
        }
      };
    }
    option.series[0].name = "Time";
    option.series[0].data = data.map((item, index) => {
      return { value: item.time };
    });
    option.series[1].name = "Distance";
    option.series[1].data = data.map((item, index) => {
      return { value: item.distance };
    });
    option.series[2].name = "Elevation";
    option.series[2].data = data.map((item, index) => {
      return { value: item.elevation };
    });
    option.series[3].name = appContext.sportView === SPORT_TYPE.Ride ? "Speed" : "Pace";
    option.series[3].data = data.map((item, index) => {
      return { value: item.speed, pace: item.pace };
    });
    option.series[4].name = "Power";
    option.series[4].data = data.map((item, index) => {
      return { value: item.power };
    });
    option.series[5].name = "HR";
    option.series[5].data = data.map((item, index) => {
      return { value: item.hr };
    });
    return option;
  };
  
  return (
    <Space
      direction="vertical"
      size={16}
      style={{ width: "100%" }}
    >
      <div className="chart-panel-content">
        <Card title={chartTitle} bordered={false}>
          <ReactEcharts
            option={buildChart(chartData) || {}}
            style={{ height: "150px" }}
          />
        </Card>
      </div>
    </Space>
  );
};

export default AreaChart;
